/**
 * Enhanced data table with sorting, filtering, and pagination
 */

import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export interface Column<T> {
  key: keyof T | string;
  title: string;
  render?: (value: any, item: T, index: number) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  selectable?: boolean;
  selectedItems?: Set<any>;
  onSelectionChange?: (selectedItems: Set<any>) => void;
  getItemId?: (item: T, index: number) => any;
  pagination?: {
    current: number;
    total: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  actions?: {
    label: string;
    onClick: (selectedItems: Set<any>) => void;
    variant?: 'primary' | 'secondary' | 'danger';
    disabled?: boolean;
  }[];
  emptyMessage?: string;
  className?: string;
}

export function DataTable<T>({
  data,
  columns,
  loading = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearch,
  selectable = false,
  selectedItems = new Set(),
  onSelectionChange,
  getItemId = (item: T, index: number) => index,
  pagination,
  actions = [],
  emptyMessage = 'No data available',
  className = ''
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch?.(query);
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    
    const newSelection = new Set(selectedItems);
    if (checked) {
      data.forEach((item, index) => {
        newSelection.add(getItemId(item, index));
      });
    } else {
      data.forEach((item, index) => {
        newSelection.delete(getItemId(item, index));
      });
    }
    onSelectionChange(newSelection);
  };

  const handleSelectItem = (itemId: any, checked: boolean) => {
    if (!onSelectionChange) return;
    
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onSelectionChange(newSelection);
  };

  const allSelected = data.length > 0 && data.every((item, index) => 
    selectedItems.has(getItemId(item, index))
  );
  const someSelected = data.some((item, index) => 
    selectedItems.has(getItemId(item, index))
  );

  // Sort data if needed
  let sortedData = [...data];
  if (sortConfig) {
    sortedData.sort((a, b) => {
      const aValue = (a as any)[sortConfig.key];
      const bValue = (b as any)[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  return (
    <Card className={`p-6 ${className}`}>
      {/* Header with search and actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {searchable && (
            <div className="w-64">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="bg-gray-800"
              />
            </div>
          )}
          {selectedItems.size > 0 && (
            <span className="text-sm text-gray-400">
              {selectedItems.size} selected
            </span>
          )}
        </div>
        
        {actions.length > 0 && selectedItems.size > 0 && (
          <div className="flex gap-2">
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'secondary'}
                size="sm"
                onClick={() => action.onClick(selectedItems)}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {selectable && (
                <th className="text-left py-3 px-4 w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = someSelected && !allSelected;
                      }
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded bg-gray-800 border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-2"
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`py-3 px-4 text-sm font-medium text-gray-400 uppercase tracking-wider ${
                    column.align === 'center' ? 'text-center' : 
                    column.align === 'right' ? 'text-right' : 'text-left'
                  } ${column.sortable ? 'cursor-pointer hover:text-white' : ''}`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key as string)}
                >
                  <div className="flex items-center gap-1">
                    <span>{column.title}</span>
                    {column.sortable && (
                      <span className="text-xs">
                        {sortConfig?.key === column.key ? (
                          sortConfig.direction === 'asc' ? '↑' : '↓'
                        ) : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td 
                  colSpan={columns.length + (selectable ? 1 : 0)} 
                  className="text-center py-12"
                >
                  <LoadingSpinner />
                </td>
              </tr>
            ) : sortedData.length > 0 ? (
              sortedData.map((item, index) => {
                const itemId = getItemId(item, index);
                const isSelected = selectedItems.has(itemId);
                
                return (
                  <tr
                    key={itemId}
                    className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                      isSelected ? 'bg-blue-900/20' : ''
                    }`}
                  >
                    {selectable && (
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectItem(itemId, e.target.checked)}
                          className="rounded bg-gray-800 border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-2"
                        />
                      </td>
                    )}
                    {columns.map((column, columnIndex) => (
                      <td
                        key={columnIndex}
                        className={`py-3 px-4 text-sm ${
                          column.align === 'center' ? 'text-center' : 
                          column.align === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {column.render ? (
                          column.render((item as any)[column.key], item, index)
                        ) : (
                          <span className="text-white">
                            {String((item as any)[column.key] || '--')}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td 
                  colSpan={columns.length + (selectable ? 1 : 0)} 
                  className="text-center py-12 text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>Show</span>
            <select
              value={pagination.pageSize}
              onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
              className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => pagination.onPageChange(pagination.current - 1)}
              disabled={pagination.current <= 1}
            >
              Previous
            </Button>
            
            <span className="text-sm text-gray-400">
              Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
            </span>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={() => pagination.onPageChange(pagination.current + 1)}
              disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}